import classNames from 'classnames'
import { forwardRef } from 'react'
import { TabsList, TabsPanel, TabsRoot, TabsTab } from '../TabsLike'
import * as styles from './styles'
import {
  type SidebarTabsListProps,
  type SidebarTabsPanelProps,
  type SidebarTabsRootProps,
  type SidebarTabsTabProps,
} from './types'

export { selectors as sidebarTabsSelectors } from './styles'

/** @note `<SidebarTabs.Root>` is intentionally left unstyled, because there’s no universally-correct styling */
const Root = forwardRef<HTMLDivElement, SidebarTabsRootProps>(
  function Root(props, ref) {
    const { className, ...remainingProps } = props

    return (
      <TabsRoot
        ref={ref}
        {...remainingProps}
        className={classNames(styles.selectors.root, className)}
      />
    )
  }
)
Root.displayName = 'Lapidary: SidebarTabs.Root'

/**
 * The row of tabs. It MUST NOT contain anything but `<SidebarTabs.Tab>`s.
 */
const List = forwardRef<HTMLDivElement, SidebarTabsListProps>(
  function List(props, ref) {
    const { className, ...remainingProps } = props

    return (
      <TabsList
        css={styles.list}
        ref={ref}
        {...remainingProps}
        className={classNames(styles.selectors.list, className)}
      />
    )
  }
)
List.displayName = 'Lapidary: SidebarTabs.List'

const Tab = forwardRef<HTMLButtonElement, SidebarTabsTabProps>(
  function Tab(props, ref) {
    const { className, ...remainingProps } = props

    return (
      <TabsTab
        css={styles.tab}
        ref={ref}
        {...remainingProps}
        className={classNames(styles.selectors.tab, className)}
      />
    )
  }
)
Tab.displayName = 'Lapidary: SidebarTabs.Tab'

/** @note `<SidebarTabs.Panel>` intentionally has minimal styling, because there’s no universally-correct styling */
const Panel = forwardRef<HTMLDivElement, SidebarTabsPanelProps>(
  function Panel(props, ref) {
    const { className, ...remainingProps } = props

    return (
      <TabsPanel
        css={styles.panel}
        ref={ref}
        {...remainingProps}
        className={classNames(styles.selectors.panel, className)}
      />
    )
  }
)
Panel.displayName = 'Lapidary: SidebarTabs.Panel'

/**
 * @example
 * <SidebarTabs.Root onValueChange={setTab} value={tab}>
 *   <SidebarTabs.List aria-label="Post details">
 *     <SidebarTabs.Tab value="comments">Comments</SidebarTabs.Tab>
 *     <SidebarTabs.Tab value="history">History</SidebarTabs.Tab>
 *   </SidebarTabs.List>
 *   <SidebarTabs.Panel value="comments">…</SidebarTabs.Panel>
 *   <SidebarTabs.Panel value="history">…</SidebarTabs.Panel>
 * </SidebarTabs.Root>
 */
export const SidebarTabs = { List, Panel, Root, Tab } as const
